/* eslint-disable semi */
/**
 * Custom hook to fetch data from an API and return the data, loading state and error.
 *
 * @template T - The type of the data returned from the API.
 * @param {string} url - The URL of the API endpoint.
 * @param {} [params] - Optional request parameters for the API call.
 * @returns {{ data: T | null, loading: boolean, error: string | null }} - The fetched data, loading state, and error message.
 */

import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { PageResult } from '@osdk/client';

import { formValuesToFoundryParams, sortByColumn, } from './util';
import axios from 'axios';
import { ReportContext, getDefaultReportFilter } from '../../../context/ReportProvider';
import { store } from '../../../../../stores/store';

export interface UseReportResponse<T> {
  data: T[] | null; // The fetched data
  loading: boolean; // Loading state
  error: string | null; // Error message
  sortKey: string | null; // Current sort key
  setSortKey: (key: string | null) => void; // Function to set the sort key
  sortOrder: 'ASC' | 'DESC'; // Current sort order
  setSortOrder: (order: 'ASC' | 'DESC') => void; // Function to set the sort order
  paramsToUse: { departmentId: number[] | string[], hospitalId: number[] | string[] }; // Parameters to use for the API call
  setParamsToUse: (params: { departmentId: number[] | string[], hospitalId: number[] | string[] }) => void; // Function to set the parameters
  page: number; // Current page number
  setPage: (page: number) => void; // Function to set the page number
  totalCount: string; // Total count of items
  nextPageToken: string | undefined; // Token for the next page
  setNextPageToken: (pageToken: string | undefined) => void; // Function to set the next page pageToken
  previousPageToken: string | undefined; // Token for the previous page
  setPreviousPageToken: (pageToken: string | undefined) => void; // Function to set the previous page pageToken
  dataLastRefreshed: string | null; // The date the data was last refreshed
  fetchObjects: () => Promise<PageResult<T>>; // Function to fetch objects
  goToNextPage: () => Promise<void>; // Function to go to the next page
  goToPreviousPage: () => Promise<void>; // Function to go to the previous page
}

// Custom hook to fetch data from an API and return the data, loading state and error

const useReport = <T,>(objectType: string, objectURL?: string, defaultParams?: {}) => {
  // We should consider modifying this hook to populate our "reportContext" through our new ReportProvider so we can manage with less drilling.
  // Small refactor work forthcoming in another tech story.
  // const authToken = process.env.REACT_APP_FOUNDRY_API_TOKEN
  const url = process.env.REACT_APP_FOUNDRY_BASE_API ?? ''
  const apigeeUrl = process.env.REACT_APP_APIGEE_URL ?? ''

  // const [searchParams, setSearchParams] = useSearchParams();
  const initialParams = {
    departmentId: [store.commonStore.departmentId].filter((id): id is number => id !== undefined),
    hospitalId: [store.commonStore.hospitalId].filter((id): id is number => id !== undefined),
  }
  const [data, setData] = useState<T[] | null>(null)
  const [totalCount, setTotalCount] = useState<string>('0')
  const [pageTokens, setPageTokens] = useState<string[]>([]) // first page pageToken is empty string
  const [nextPageToken, setNextPageToken] = useState<string | undefined>(undefined)
  const [previousPageToken, setPreviousPageToken] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [paramsToUse, setParamsToUse] = useState<{
    departmentId: number[] | string[]
    hospitalId: number[] | string[]
  }>(initialParams)
  const [sortKey, setSortKey] = useState<string | null>(null)
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC')
  const [page, setPage] = useState<number>(1)
  const reportContext = useContext(ReportContext)
  const axiosTimeoutMs = 20000 // 20s timeout for each request

  const fetchObjects = useCallback(
    async (pageToken?: string): Promise<PageResult<T>> => {
      if (reportContext?.reportCurrentPageNumber === 1 && !pageToken) {
        setPageTokens([])
        setPage(1)
      }

      const filterParams =
        reportContext?.reportFilter && Object.keys(reportContext.reportFilter).length > 0
          ? reportContext.reportFilter
          : initialParams

      const sortParams =
        sortByColumn(
          typeof reportContext?.reportFilter === 'object' && reportContext?.reportFilter !== null
            ? reportContext.reportFilter
            : {}
        ) ?? undefined

      const pageTokenValue =
        reportContext?.reportCurrentPageNumber !== 1 && !pageToken
          ? nextPageToken ?? pageTokens[page - 1]
          : undefined

      const data = {
        where: {
          type: 'and',
          value: formValuesToFoundryParams(filterParams, objectType),
        },
        select: [],
        excludeRid: true,
        orderBy: sortParams,
        pageSize: reportContext?.reportPageSize,
        pageToken: pageTokenValue,

      }
      setLoading(true)

      const endpointUrl = objectURL ? objectURL : `${apigeeUrl}/${objectType}/search`;
      try {
        // console.log(authToken);

        let redisKey = store.commonStore.redisKey; // Get the redis key from the store
        const response = await axios.post(endpointUrl, defaultParams ?? data, {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Redis-Key': redisKey?.toString() ?? '',
          },
          timeout: axiosTimeoutMs,
        })
        setData(objectType === 'UsageTransfer' || objectType === 'QsightPrdlegUsageTransfer' ? response.data.data as T[] : response.data as T[]);
        const isUsageTransferFamily = (t: string) => t === 'UsageTransfer' || t === 'QsightPrdlegUsageTransfer';
        if (isUsageTransferFamily(objectType)) {
          setTotalCount(response.data.totalCount)
          setNextPageToken(response.data.nextPageToken)
          setPreviousPageToken(response.data.previousPageToken)
          if (
            response.data.data &&
            response.data.data.length > 0 &&
            response.data.data[0].dataLastRefreshed
          ) {
            reportContext?.setDataLastRefreshed(response.data.data[0].dataLastRefreshed)
          }
          reportContext?.setReportTotalCount(response.data.totalCount)
        }
        setLoading(false)
        return response.data as PageResult<T>
      } catch (err) {
        console.error('Error fetching QsightTestData:', err)
        setError('Error fetching data')
        setData(null)
        setLoading(false)
        return Promise.reject(err) // Return a rejected promise to handle the error in the calling function
      }
    },
    [objectType, paramsToUse, reportContext, url]
  )

  const goToNextPage = async () => {
    if (nextPageToken) {
      const newPageTokens = [...pageTokens, nextPageToken]
      setPageTokens(newPageTokens)
      setPage(prev => prev + 1)
      // await fetchObjects(nextPageToken);
    }
  }

  const goToPreviousPage = async () => {
    if (page > 1) {
      const newPageTokens = pageTokens.slice(0, -1)
      setPageTokens(newPageTokens)
      setPage(prev => prev - 1)
      // await fetchObjects(newPageTokens[newPageTokens.length - 1]);
    }
  }

  // Track last fetch signature to avoid redundant identical requests
  const lastFetchSigRef = useRef<string | null>(null)

  useEffect(() => {
    // Determine if this navigation is for a memorized report
    const sp = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '')
    const hasMemId = !!sp.get('memid')

    // Wait for memorized payload before initial fetch when memid is present
    const hasMemPayload = !!reportContext?.memorizedData &&
      Object.keys(reportContext.memorizedData).length > 0

    // If the current report route id hasn't been established yet by the layout,
    // defer any fetching. This avoids firing a default fetch before the provider
    // can hydrate persisted filters for the specific report id, which leads to
    // duplicate requests (default -> hydrated).
    const hasReportId = !!reportContext?.currentReportId

    // If filters are retained in sessionStorage but context hasn't hydrated yet,
    // skip the default initial fetch to avoid double-calling with defaults.
    const hasPersistedFilter = (() => {
      try {
        const id = reportContext?.currentReportId
        if (!id) return false
        return !!sessionStorage.getItem(`reportFilter:${id}`)
      } catch {
        return false
      }
    })()

    const isContextFilterDefault = (() => {
      try {
        const current = reportContext?.reportFilter ?? {}
        const def = getDefaultReportFilter(store.commonStore) as unknown
        return JSON.stringify(current) === JSON.stringify(def)
      } catch {
        return false
      }
    })()

    // Ensure we have department context; prefer context filter over local params
    const deptIdsFromFilter = (() => {
      try {
        const rf: any = reportContext?.reportFilter ?? {}
        const v = rf?.departmentId
        return Array.isArray(v) ? v : []
      } catch { return [] }
    })()
    const effectiveDeptIds = (deptIdsFromFilter?.length ? deptIdsFromFilter : paramsToUse?.departmentId) ?? []
    const shouldFetch = (
      effectiveDeptIds.length > 0 &&
      hasReportId &&
      !(hasMemId && !hasMemPayload) &&
      !(hasPersistedFilter && isContextFilterDefault)
    )

    // Build a signature for this fetch (filter + paging)
    const fetchSig = JSON.stringify({
      f: reportContext?.reportFilter ?? {},
      ps: reportContext?.reportPageSize,
      p: reportContext?.reportCurrentPageNumber,
    })

    if (!shouldFetch) {
      // Defer fetch until prerequisites are met; ensure UI isn't stuck loading
      setLoading(false)
      return
    }

    // Avoid duplicate fetches when signature hasn't changed
    if (lastFetchSigRef.current === fetchSig) {
      setLoading(false)
      return
    }
    lastFetchSigRef.current = fetchSig

    fetchObjects()
  }, [
    reportContext?.reportFilter,
    reportContext?.reportPageSize,
    reportContext?.reportCurrentPageNumber,
    reportContext?.memorizedData,
    paramsToUse?.departmentId,
    reportContext?.currentReportId
  ])

  return {
    data,
    loading,
    error,
    sortKey,
    setSortKey,
    sortOrder,
    setSortOrder,
    paramsToUse,
    setParamsToUse,
    page,
    setPage,
    totalCount,
    nextPageToken,
    setNextPageToken,
    previousPageToken,
    setPreviousPageToken,
    fetchObjects,
    goToNextPage,
    goToPreviousPage,
    dataLastRefreshed: reportContext?.dataLastRefreshed ?? null,
  }
};

export default useReport;
